import {
  finalizeUploadedPhoto,
  requestPhotoUploadUrl,
  uploadFileToSignedUrl,
} from '@/features/photo-gallery/api/photo-gallery-api';
import type { GalleryPhoto, PhotoSourceType } from '@/features/photo-gallery/types';

export interface BackgroundUploadResult {
  photoId: number;
  photoUuid: string;
  galleryPhoto: GalleryPhoto;
}

/**
 * Upload a single file to GCS via the generic photos endpoint and return the
 * resulting Photo metadata. Used to pre-upload attachments while the user is
 * still filling out the hotspot form, so submission only has to link the
 * existing Photo to the new item.
 */
export const uploadProjectPhotoInBackground = async (params: {
  projectId: number;
  file: File;
  sourceType?: PhotoSourceType;
  onProgress?: (pct: number) => void;
}): Promise<BackgroundUploadResult> => {
  const { projectId, file, sourceType = 'gallery', onProgress } = params;

  const upload = await requestPhotoUploadUrl({
    project_id: projectId,
    file_name: file.name,
    content_type: file.type || 'application/octet-stream',
    source_type: sourceType,
  });

  await uploadFileToSignedUrl(file, upload.signed_upload_url, onProgress);

  const galleryPhoto = await finalizeUploadedPhoto({
    project_id: projectId,
    photo_uuid: upload.photo_uuid,
    gcs_object_path: upload.gcs_object_path,
    original_file_name: file.name,
    mime_type: file.type || 'application/octet-stream',
    file_size: file.size,
    source_type: sourceType,
  });

  return {
    photoId: galleryPhoto.id,
    photoUuid: galleryPhoto.uuid,
    galleryPhoto,
  };
};
